import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { Plus, Edit, RefreshCw, Home, AlertTriangle, Trash2 } from 'lucide-react';
import { Topbar } from '@/components/layout/Topbar';
import { Dialog } from '@/components/ui/Dialog';
import { useLotsImmo, useCreateLotImmo, useUpdateLotImmo, useDeleteLotImmo, usePrograms } from '@/hooks/useImmobilier';
import {
  STATUT_LOT_IMMO_LABELS, TYPE_BIEN_LABELS,
  type LotImmo, type StatutLotImmo, type TypeBien,
} from '@/types';
import { formatMontant, formatNumber } from '@/utils/currency';
import { extractError } from '@/api/client';
import { useAuthStore } from '@/stores/authStore';
import { demander } from '@/components/ui/Fenetres';

const STATUT_BADGE: Record<StatutLotImmo, string> = {
  disponible: 'ds-bd ds-bd-s',
  reserve:    'ds-bd ds-bd-w',
  vendu:      'ds-bd ds-bd-i',
  bloque:     'ds-bd ds-bd-d',
};

export const LotsImmoPage = () => {
  const filiale = useAuthStore((s) => s.filiale);
  const [statut, setStatut]         = useState<StatutLotImmo | ''>('');
  const [typeBien, setTypeBien]     = useState<TypeBien | ''>('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing]       = useState<LotImmo | undefined>();
  const [error, setError]           = useState<string | null>(null);

  const { data, isLoading, refetch } = useLotsImmo({ statut: statut || undefined, typeBien: typeBien || undefined, limit: 200 });
  const lots = data?.data ?? [];
  const remove = useDeleteLotImmo();

  const openCreate = () => { setEditing(undefined); setDialogOpen(true); };
  const openEdit   = (l: LotImmo) => { setEditing(l); setDialogOpen(true); };

  const onRemove = async (id: string, code: string) => {
    if (!(await demander.confirmer({ titre: `Supprimer le lot "${code}" ?`, confirmer: 'Supprimer', danger: true }))) return;
    setError(null);
    try { await remove.mutateAsync(id); }
    catch (err) { setError(extractError(err).message); }
  };

  const count = (s: StatutLotImmo) => lots.filter((l) => l.statut === s).length;
  const valeurPortefeuille = lots.filter((l) => l.statut === 'disponible' || l.statut === 'reserve').reduce((s, l) => s + Number(BigInt(l.prixVenteCents)) / 100, 0);

  return (
    <>
      <Topbar
        title="Lots immobiliers"
        subtitle={filiale ? `${lots.length} lot(s) · ${filiale.fullName}` : 'Lots'}
        actions={
          <>
            <button className="ds-btn ds-btn-g ds-btn-sm" onClick={() => void refetch()}><RefreshCw size={14} className={isLoading ? 'animate-spin' : ''} /></button>
            <button className="ds-btn ds-btn-p" onClick={openCreate}><Plus size={14} /> Nouveau lot</button>
          </>
        }
      />
      <div className="ds-content">
        {error && <div className="ds-alert-strip ds-alert-d" style={{ marginBottom: 16 }}><span className="ds-alert-ico"><AlertTriangle size={18} /></span><div className="ds-alert-txt">{error}</div></div>}

        <div className="ds-kpi-row" style={{ marginBottom: 16 }}>
          <div className="ds-kr kr-c1"><div className="ds-kr-ico">🏠</div><div className="ds-kr-val">{lots.length}</div><div className="ds-kr-lbl">Total lots</div></div>
          <div className="ds-kr kr-c3"><div className="ds-kr-ico">✅</div><div className="ds-kr-val">{count('disponible')}</div><div className="ds-kr-lbl">Disponibles</div></div>
          <div className="ds-kr kr-c2"><div className="ds-kr-ico">📝</div><div className="ds-kr-val">{count('reserve')}</div><div className="ds-kr-lbl">Réservés</div></div>
          <div className="ds-kr kr-c4"><div className="ds-kr-ico">🤝</div><div className="ds-kr-val">{count('vendu')}</div><div className="ds-kr-lbl">Vendus</div></div>
          <div className="ds-kr kr-c5"><div className="ds-kr-ico">💰</div><div className="ds-kr-val">{formatNumber(valeurPortefeuille)}</div><div className="ds-kr-lbl">Valeur dispo+résa (FCFA)</div></div>
        </div>

        <div className="ds-filters">
          <select className="ds-fi" value={statut} onChange={(e) => setStatut(e.target.value as StatutLotImmo | '')} style={{ width: 'auto' }}>
            <option value="">Tous statuts</option>
            {(Object.keys(STATUT_LOT_IMMO_LABELS) as StatutLotImmo[]).map((s) => <option key={s} value={s}>{STATUT_LOT_IMMO_LABELS[s]}</option>)}
          </select>
          <select className="ds-fi" value={typeBien} onChange={(e) => setTypeBien(e.target.value as TypeBien | '')} style={{ width: 'auto' }}>
            <option value="">Tous types</option>
            {(Object.keys(TYPE_BIEN_LABELS) as TypeBien[]).map((t) => <option key={t} value={t}>{TYPE_BIEN_LABELS[t]}</option>)}
          </select>
        </div>

        <div className="ds-table-panel">
          <div className="ds-table-header"><div><div className="ds-table-title">Lots</div><div className="ds-table-sub">{lots.length} lot{lots.length > 1 ? 's' : ''}</div></div></div>
          <table className="ds-tbl">
            <thead><tr>
              <th style={{ width: 100 }}>Code</th>
              <th>Désignation</th>
              <th style={{ width: 100 }}>Type</th>
              <th>Programme</th>
              <th className="ds-am" style={{ width: 90 }}>Surface</th>
              <th className="ds-am">Prix vente FCFA</th>
              <th style={{ width: 100 }}>Statut</th>
              <th style={{ width: 80 }}></th>
            </tr></thead>
            <tbody>
              {isLoading && <tr><td colSpan={8} style={{ textAlign: 'center', padding: 32, color: 'var(--tx-3)' }}>Chargement…</td></tr>}
              {!isLoading && lots.length === 0 && (
                <tr><td colSpan={8} style={{ textAlign: 'center', padding: 48 }}>
                  <Home size={28} style={{ margin: '0 auto 8px', color: 'var(--tx-3)' }} />
                  <p style={{ fontSize: 13, color: 'var(--tx-3)' }}>Aucun lot</p>
                  <button className="ds-btn ds-btn-p ds-btn-sm" style={{ marginTop: 12 }} onClick={openCreate}><Plus size={12} /> Créer le premier lot</button>
                </td></tr>
              )}
              {!isLoading && lots.map((l) => (
                <tr key={l.id}>
                  <td><span className="ds-ref-code">{l.code}</span></td>
                  <td>
                    <div className="ds-entry-label">{l.designation ?? '—'}</div>
                    {(l.nbPieces || l.etage !== null) && <div className="ds-entry-sub">{l.nbPieces ? `${l.nbPieces} pces` : ''}{l.etage !== null && l.etage !== undefined ? ` · Étage ${l.etage}` : ''}</div>}
                  </td>
                  <td style={{ fontSize: 12 }}>{TYPE_BIEN_LABELS[l.typeBien]}</td>
                  <td style={{ fontSize: 12 }}>{l.programme ? `${l.programme.code} — ${l.programme.nom}` : '—'}</td>
                  <td className="ds-am">{l.surfaceUtileM2 ? `${l.surfaceUtileM2} m²` : '—'}</td>
                  <td className="ds-am" style={{ fontWeight: 600 }}>{formatMontant(l.prixVenteCents)}</td>
                  <td><span className={STATUT_BADGE[l.statut]}>{STATUT_LOT_IMMO_LABELS[l.statut]}</span></td>
                  <td><div style={{ display: 'flex', gap: 4 }}>
                    <button onClick={() => openEdit(l)} className="ds-row-btn" title="Modifier"><Edit size={13} /></button>
                    <button onClick={() => onRemove(l.id, l.code)} className="ds-row-btn" title="Supprimer" disabled={remove.isPending}><Trash2 size={13} /></button>
                  </div></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <LotFormDialog open={dialogOpen} onClose={() => setDialogOpen(false)} lot={editing} />
    </>
  );
};

const LotFormDialog = ({ open, onClose, lot }: { open: boolean; onClose: () => void; lot?: LotImmo }) => {
  const isEdit = !!lot;
  const [code, setCode]                 = useState('');
  const [programmeId, setProgrammeId]   = useState('');
  const [designation, setDesignation]   = useState('');
  const [typeBien, setTypeBien]         = useState<TypeBien>('appartement');
  const [surfaceUtile, setSurfaceUtile] = useState('');
  const [surfaceTotale, setSurfaceTotale] = useState('');
  const [nbPieces, setNbPieces]         = useState('');
  const [etage, setEtage]               = useState('');
  const [prix, setPrix]                 = useState('');
  const [statut, setStatut]             = useState<StatutLotImmo>('disponible');
  const [notes, setNotes]               = useState('');
  const [error, setError]               = useState<string | null>(null);

  const { data: programmesData } = usePrograms({ limit: 200 });
  const programmes = programmesData?.data ?? [];
  const create = useCreateLotImmo();
  const update = useUpdateLotImmo();

  useEffect(() => {
    if (!open) return;
    if (lot) {
      setCode(lot.code); setProgrammeId(lot.programmeId ?? ''); setDesignation(lot.designation ?? '');
      setTypeBien(lot.typeBien);
      setSurfaceUtile(lot.surfaceUtileM2 ?? ''); setSurfaceTotale(lot.surfaceTotaleM2 ?? '');
      setNbPieces(lot.nbPieces?.toString() ?? ''); setEtage(lot.etage?.toString() ?? '');
      setPrix(String(Number(BigInt(lot.prixVenteCents)) / 100));
      setStatut(lot.statut); setNotes(lot.notes ?? '');
    } else {
      setCode(''); setProgrammeId(''); setDesignation(''); setTypeBien('appartement');
      setSurfaceUtile(''); setSurfaceTotale(''); setNbPieces(''); setEtage(''); setPrix('');
      setStatut('disponible'); setNotes('');
    }
    setError(null);
  }, [open, lot]);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      const payload = {
        code: code.trim(),
        programmeId: programmeId || undefined,
        designation: designation.trim() || undefined,
        typeBien,
        surfaceUtileM2:  surfaceUtile ? parseFloat(surfaceUtile) : undefined,
        surfaceTotaleM2: surfaceTotale ? parseFloat(surfaceTotale) : undefined,
        nbPieces: nbPieces ? parseInt(nbPieces, 10) : undefined,
        etage:    etage ? parseInt(etage, 10) : undefined,
        prixVenteCents: Math.round(parseFloat(prix || '0') * 100),
        statut,
        notes: notes.trim() || undefined,
      };
      if (isEdit && lot) await update.mutateAsync({ id: lot.id, payload });
      else await create.mutateAsync(payload);
      onClose();
    } catch (err) { setError(extractError(err).message); }
  };

  const canSubmit = code.trim() && parseFloat(prix || '0') >= 0;

  return (
    <Dialog open={open} onClose={onClose} title={isEdit ? `Modifier — ${lot?.code}` : 'Nouveau lot immobilier'} subtitle="David & Co" size="lg">
      <form onSubmit={onSubmit} className="space-y-3">
        {error && <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700"><AlertTriangle className="inline size-3 mr-1" />{error}</div>}

        <div className="grid grid-cols-3 gap-3">
          <Field label="Code *"><input value={code} onChange={(e) => setCode(e.target.value)} className="ds-fi" required style={{ width: '100%' }} placeholder="L-A101" /></Field>
          <Field label="Type *"><select value={typeBien} onChange={(e) => setTypeBien(e.target.value as TypeBien)} className="ds-fi" style={{ width: '100%' }}>{(Object.keys(TYPE_BIEN_LABELS) as TypeBien[]).map((t) => <option key={t} value={t}>{TYPE_BIEN_LABELS[t]}</option>)}</select></Field>
          <Field label="Statut"><select value={statut} onChange={(e) => setStatut(e.target.value as StatutLotImmo)} className="ds-fi" style={{ width: '100%' }}>{(Object.keys(STATUT_LOT_IMMO_LABELS) as StatutLotImmo[]).map((s) => <option key={s} value={s}>{STATUT_LOT_IMMO_LABELS[s]}</option>)}</select></Field>
        </div>

        <Field label="Désignation"><input value={designation} onChange={(e) => setDesignation(e.target.value)} className="ds-fi" style={{ width: '100%' }} placeholder="T3 75m² avec balcon" /></Field>

        <Field label="Programme">
          <select value={programmeId} onChange={(e) => setProgrammeId(e.target.value)} className="ds-fi" style={{ width: '100%' }}>
            <option value="">— Lot isolé (hors programme) —</option>
            {programmes.map((p) => <option key={p.id} value={p.id}>{p.code} — {p.nom}</option>)}
          </select>
        </Field>

        <div className="grid grid-cols-4 gap-3">
          <Field label="Surface utile (m²)"><input type="number" min="0" step="0.01" value={surfaceUtile} onChange={(e) => setSurfaceUtile(e.target.value)} className="ds-fi" style={{ width: '100%' }} /></Field>
          <Field label="Surface totale (m²)"><input type="number" min="0" step="0.01" value={surfaceTotale} onChange={(e) => setSurfaceTotale(e.target.value)} className="ds-fi" style={{ width: '100%' }} /></Field>
          <Field label="Nb pièces"><input type="number" min="0" value={nbPieces} onChange={(e) => setNbPieces(e.target.value)} className="ds-fi" style={{ width: '100%' }} /></Field>
          <Field label="Étage"><input type="number" value={etage} onChange={(e) => setEtage(e.target.value)} className="ds-fi" style={{ width: '100%' }} /></Field>
        </div>

        <Field label="Prix vente (FCFA) *"><input type="number" min="0" step="1" value={prix} onChange={(e) => setPrix(e.target.value)} className="ds-fi" required style={{ width: '100%' }} placeholder="85000000" /></Field>

        <Field label="Notes"><textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className="ds-fi" style={{ width: '100%' }} /></Field>

        <div className="flex justify-end gap-2 border-t border-gray-100 pt-3">
          <button type="button" className="ds-btn ds-btn-g" onClick={onClose}>Annuler</button>
          <button type="submit" className="ds-btn ds-btn-p" disabled={!canSubmit || create.isPending || update.isPending}>{isEdit ? 'Enregistrer' : 'Créer le lot'}</button>
        </div>
      </form>
    </Dialog>
  );
};

const Field = ({ label, children }: { label: string; children: ReactNode }) => (
  <div><label className="mb-1 block text-xs font-medium text-gray-600">{label}</label>{children}</div>
);
